import { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { TranslationsFunc, useTranslations } from '@lang';
import { useRoomPageSettings } from '@features/rooms';
import { AccommodationListItem } from 'voyager';
import { mapDiscountToDisplayObject } from '@utils/mapDiscountToDisplayObject';
import useRooms from '../useRooms';
import { Room } from '../useRooms/types';

export const buildOccupancyLabels = (room: Room, t: TranslationsFunc): string[] => {
  const labels: string[] = [];

  if (typeof room.maxAdults === 'number' && room.maxAdults > 0) {
    labels.push(t('basket.adults', { adults: room.maxAdults }));
  }

  if (typeof room.maxChildren === 'number' && room.maxChildren > 0) {
    labels.push(t('basket.children', { children: room.maxChildren }));
  }

  if (labels.length === 0 && typeof room.maxOccupancy === 'number' && room.maxOccupancy > 0) {
    labels.push(t('common.numberOfGuests', { guestsAmount: room.maxOccupancy }));
  }

  return labels;
};

const toPromotionName = (room: Room, t: TranslationsFunc): string | undefined => {
  if (room.discount && !room.unAvailable) {
    return room.discount.name;
  }

  return room.displayUrgencyMessaging && room.quantity
    ? t('room.hurryMessage', { quantity: room.quantity })
    : undefined;
};

export const mapRoomToListItem = (
  room: Room,
  t: TranslationsFunc,
  fallbackImageUrl?: string
): AccommodationListItem => ({
  id: room.id,
  name: room.title,
  imageUrl: room.imageUrl || fallbackImageUrl || undefined,
  price: room.price
    ? { amount: room.price.amount, currencyCode: room.price.currencyCode }
    : undefined,
  discount: room.unAvailable ? undefined : mapDiscountToDisplayObject(room.discount),
  occupancyLabels: buildOccupancyLabels(room, t),
  promotionName: toPromotionName(room, t),
  unavailable: room.unAvailable,
  unavailableLabel: t('common.unavailable'),
});

export function useRoomsList(journeyParams: ParsedJourneyParams, language: string) {
  const { t } = useTranslations();
  const { content, isFetching: isFetchingContent } = useRoomPageSettings(
    language,
    t('common.room')
  );
  const { rooms, isFetching, isAvailabilityUnknown, isRetryingAvailability, retryAvailability } =
    useRooms(journeyParams, language);

  const fallbackImageUrl = content?.heroBackgroundImage ?? undefined;

  const listItems: AccommodationListItem[] = rooms.map((room) =>
    mapRoomToListItem(room, t, fallbackImageUrl)
  );

  return {
    listItems,
    content,
    isFetching: isFetching || isFetchingContent,
    isAvailabilityUnknown,
    isRetrying: isRetryingAvailability,
    retry: retryAvailability,
  };
}
